/**
 * TEMPORARY diagnostic logging for the multi-location infinite-request loop
 * (dev-only investigation, 2026-07-23). Logs are prefixed "[LOOP]" so they can
 * be filtered in the console and grepped out of the tree when removed.
 *
 * What we're trying to prove: which link in this suspected cycle moves —
 *   service-adjustment fetch → 403 → access refresh / context re-render →
 *   new fetchWithAuth identity → effect refires → fetch again …
 */

import { useRef } from "react";

const counters: Record<string, number> = {};

export function loopLog(tag: string, detail?: unknown): number {
  counters[tag] = (counters[tag] ?? 0) + 1;
  const n = counters[tag];
  // eslint-disable-next-line no-console
  console.warn(
    `[LOOP] ${tag} #${n} @${new Date().toISOString().slice(11, 23)}`,
    detail ?? "",
  );
  if (n === 10 || n === 25 || n === 50) {
    // eslint-disable-next-line no-console
    console.warn(`[LOOP] ⚠️ ${tag} fired ${n}× — counters:`, { ...counters });
  }
  return n;
}

/** Track whether a dependency's identity changed between effect runs. */
export function identityChanges(
  prev: Record<string, unknown>,
  next: Record<string, unknown>,
): string[] {
  return Object.keys(next).filter((k) => prev[k] !== next[k]);
}

/**
 * Render-loop probe: call inside a provider/component body with its context
 * value (or any deps object). Logs [LOOP] "<tag> value churn" naming the keys
 * whose identity changed since the previous render — the looping provider
 * will identify itself and the churning field.
 */
export function useValueChurnProbe(
  tag: string,
  obj: Record<string, unknown>,
): void {
  const prevRef = useRef<Record<string, unknown>>({});
  const changed = identityChanges(prevRef.current, obj);
  prevRef.current = obj;
  if (changed.length > 0) {
    loopLog(`${tag} value churn`, changed);
  }
}
